import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Zap } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const DealSequencesTab = ({ dealId, onRefresh }) => {
  const [dealSequences, setDealSequences] = useState([]);
  const [allSequences, setAllSequences] = useState([]);
  const [enrolling, setEnrolling] = useState(false);

  const fetchSequences = useCallback(async () => {
    try {
      const [dealSeqRes, allSeqRes] = await Promise.all([
        axios.get(`${API}/api/admin/deals/${dealId}/sequences`, { withCredentials: true }),
        axios.get(`${API}/api/admin/sequences`, { withCredentials: true })
      ]);
      setDealSequences(dealSeqRes.data.enrollments || []);
      setAllSequences(allSeqRes.data.sequences || []);
    } catch (err) {
      console.error('Failed to fetch sequences:', err);
    }
  }, [dealId]);

  useEffect(() => {
    fetchSequences();
  }, [fetchSequences]);

  const enrollInSequence = async (sequenceId) => {
    setEnrolling(true);
    try {
      await axios.post(`${API}/api/admin/sequences/enroll`, {
        deal_id: dealId,
        sequence_id: sequenceId
      }, { withCredentials: true });
      fetchSequences();
      onRefresh();
    } catch (err) {
      console.error('Failed to enroll:', err);
      alert(err.response?.data?.detail || 'Failed to enroll in sequence');
    }
    setEnrolling(false);
  };

  const unenrollFromSequence = async (enrollmentId) => {
    if (!window.confirm('Remove this deal from the sequence?')) return;
    try {
      await axios.delete(`${API}/api/admin/sequences/enrollments/${enrollmentId}`, { withCredentials: true });
      fetchSequences();
    } catch (err) {
      console.error('Failed to unenroll:', err);
    }
  };

  const availableSequences = allSequences.filter(
    s => s.is_active && !dealSequences.some(ds => ds.sequence_id === s.id && ds.status === 'active')
  );
  const activeEnrollments = dealSequences.filter(e => e.status === 'active');
  const pastEnrollments = dealSequences.filter(e => e.status !== 'active');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Enroll in Sequence */}
      <div style={{ background: '#FAFBFC', borderRadius: 8, padding: '1rem', border: '1px solid #E5E7EB' }}>
        <h4 style={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
          Enroll in Sequence
        </h4>
        {availableSequences.length === 0 ? (
          <p style={{ fontSize: '0.813rem', color: '#6B7280' }}>
            {allSequences.length === 0 ? 'No sequences created yet. Create one in the Sequences tab.' : 'Already enrolled in all available sequences.'}
          </p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {availableSequences.map(seq => (
              <button
                key={seq.id}
                onClick={() => enrollInSequence(seq.id)}
                disabled={enrolling}
                data-testid={`enroll-seq-${seq.id}`}
                style={{
                  padding: '0.5rem 0.75rem', borderRadius: 6, fontSize: '0.75rem', fontWeight: 500,
                  border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '0.375rem', color: 'var(--navy)'
                }}
              >
                <Zap size={12} style={{ color: '#F59E0B' }} />
                {seq.name}
                <span style={{ fontSize: '0.65rem', color: '#9CA3AF' }}>({seq.steps?.length || 0} emails)</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Active Enrollments */}
      <div>
        <h4 style={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
          Active Sequences ({activeEnrollments.length})
        </h4>
        {activeEnrollments.length === 0 ? (
          <p style={{ textAlign: 'center', padding: '1rem', color: '#9CA3AF', fontSize: '0.813rem' }}>
            Not enrolled in any sequences
          </p>
        ) : activeEnrollments.map(enrollment => (
          <div key={enrollment.id} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0.75rem', background: '#fff', borderRadius: 6, border: '1px solid #E5E7EB',
            marginBottom: '0.5rem'
          }}>
            <div>
              <div style={{ fontWeight: 500, fontSize: '0.875rem', color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <Zap size={14} style={{ color: '#F59E0B' }} />
                {enrollment.sequence_name}
              </div>
              <div style={{ fontSize: '0.7rem', color: '#6B7280', marginTop: '0.125rem' }}>
                Step {enrollment.current_step + 1} of {enrollment.total_steps} •
                {enrollment.emails_sent} sent •
                Next: {enrollment.next_send_at ? new Date(enrollment.next_send_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
              </div>
            </div>
            <button
              onClick={() => unenrollFromSequence(enrollment.id)}
              style={{
                padding: '0.25rem 0.5rem', borderRadius: 4, fontSize: '0.65rem',
                border: '1px solid #FEE2E2', background: '#FEF2F2', cursor: 'pointer', color: '#DC2626'
              }}
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      {/* Completed/Cancelled History */}
      {pastEnrollments.length > 0 && (
        <div>
          <h4 style={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
            History
          </h4>
          {pastEnrollments.map(enrollment => (
            <div key={enrollment.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0.5rem 0.75rem', background: '#F9FAFB', borderRadius: 6,
              marginBottom: '0.375rem', opacity: 0.7
            }}>
              <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                {enrollment.sequence_name} • {enrollment.emails_sent} emails sent
              </div>
              <span style={{
                fontSize: '0.6rem', fontWeight: 600, padding: '2px 6px', borderRadius: 100,
                background: enrollment.status === 'completed' ? '#ECFDF5' : '#F3F4F6',
                color: enrollment.status === 'completed' ? '#059669' : '#6B7280',
                textTransform: 'capitalize'
              }}>
                {enrollment.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
